"use client";

import { Suspense, type ComponentType, type ReactNode } from "react";
import { Outlet } from "@tanstack/react-router";
import { TopNav } from "@/components/layout/TopNav";
import { StockReturnBack } from "@/components/stock/StockReturnBack";
import { SessionGate } from "@/components/layout/SessionGate";
import { PrefsSyncGate } from "@/components/layout/PrefsSyncGate";
import { HoverTip } from "@/components/HoverTip";
import { CatchUpBoot } from "@/components/maintenance/CatchUpBoot";
import { AppDialogProvider } from "@/components/ui/AppDialog";
import { PaperPill } from "@/components/wealth/PaperPill";

/**
 * Synchronous on purpose. cookies() / headers() / a database read in this file
 * made every client navigation wait for the Node half of the session gate
 * (and, on SQLite then, for its 30s busy_timeout if a writer held the lock) before
 * nested loading could paint. Credential freshness lives in SessionGate,
 * streamed as a Suspense sibling of the page slot.
 */
export function AppShell() {
  return (
    <AppDialogProvider>
      <TopNav />
      <StockReturnBack />
      <Suspense fallback={null}>
        <SessionGate />
      </Suspense>
      <PrefsSyncGate>
        <Outlet />
      </PrefsSyncGate>
      <HoverTip />
      <CatchUpBoot />
      {/* Says so on every page while this device shows the paper account. */}
      <PaperPill />
    </AppDialogProvider>
  );
}

export function AppPending() {
  return <div className="min-h-[calc(100dvh-var(--mpp-chrome-h))] bg-canvas" aria-hidden />;
}

/** Helper so section layouts keep taking `{ children }` as they did under Next. */
export function withLayout(
  Layout: ComponentType<{ children: ReactNode }>,
  Page: ComponentType,
) {
  return function LaidOut() {
    return <Layout><Page /></Layout>;
  };
}
